import { CommanderAPI } from '../../../../../redux/commander/api/CommanderAPI';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../redux/store.model';
import { getMachinesTypeSelector } from '../../../../../redux/composableDevice/selectors/composableDeviceSelectors';
import ControlUnitAPI from '../../../../../redux/controlUnit/api/ControlUnitAPI';
import { ControlUnitAPIResponse } from '../../../../../redux/controlUnit/api/ControlUnit.model';
import { getControlUnitsActiveByIdFilter } from '../../../../../redux/controlUnit/selectors';
import { postGroup } from '../../../../../redux/groups/actions/thunks';
import { Base64 } from '../../../../../util/Base64';
import { ComposableDeviceUserInterfaces } from '../../../../../redux/composableDevice/ComposableDevice.model';
import useAllMerchandisers from './useAllMerchandisers';
import { getChannelsByComposableDevice } from '../../../util/getChannelsByComposableDevice';

interface CreateMerchandiser {
  merchandiserType: string;
  commanderSerialNumber: string;
  merchandiserName: string;
}
export const useCreateMerchandiser = (groupId: string) => {
  const { merchandisers } = useAllMerchandisers({
    groupId,
  });
  const devicesInStore = merchandisers?.flatMap((m) => m.devices) ?? [];

  const [getCommanderBySerialNumber] =
    CommanderAPI.useLazyGetCommanderBySerialNumberQuery();
  const machineTypes = useAppSelector(getMachinesTypeSelector);
  const [getControlUnitsBySerialNumber] =
    ControlUnitAPI.useLazyGetControlUnitsBySerialNumberQuery();

  const dispatch = useAppDispatch();
  let errorMessage = '';

  const createMerchandiser = async (params: CreateMerchandiser) => {
    const { merchandiserType, commanderSerialNumber, merchandiserName } =
      params;
    try {
      const machineType = machineTypes.find(
        (type) => type.id === merchandiserType
      );
      if (
        machineType?.userInterface !==
        ComposableDeviceUserInterfaces.SCC_MERCHANDISE_V2
      ) {
        errorMessage = 'freshwatch.merchandiser.add.machine_type_not_valid';
        throw new Error(errorMessage);
      }
      const commanderInfo = await getCommanderBySerialNumber(
        commanderSerialNumber.trim()
      ).unwrap();
      if (!commanderInfo) {
        errorMessage = 'freshwatch.merchandiser.add.device_not_found';
      } else if (
        devicesInStore.find(
          (device) => device === Base64.decode(commanderInfo?.uuid)
        )
      ) {
        errorMessage = 'freshwatch.merchandiser.add.device_already_exists';
      }
      if (errorMessage) {
        throw new Error(errorMessage);
      }

      const commanderId = Base64.decode(commanderInfo.uuid);

      const controlUnits = await getControlUnitsBySerialNumber(
        commanderSerialNumber
      );
      if (controlUnits.error || !controlUnits?.data?.length) {
        errorMessage = 'freshwatch.merchandiser.add.control_unit_error';
        throw new Error(errorMessage);
      }

      const validControlUnits = controlUnits.data.filter(
        getControlUnitsActiveByIdFilter
      );
      const default_channel =
        machineType?.labels['merchandiser_default_channel'] || '';
      const default_image = machineType?.labels['default_image'] || '';

      const channelList = getChannelsByComposableDevice(
        machineType!,
        validControlUnits as ControlUnitAPIResponse[],
        commanderId
      );

      const group = {
        name: merchandiserName,
        parentGroupID: groupId ?? '',
        composableDevice: {
          machineTypeId: machineType?.id || '',
          channels: channelList,
          labels: {
            default_channel: default_channel,
            serial_number: commanderSerialNumber,
            imageURL: default_image,
          },
        },
        imageURL: '',
        logoURL: default_image,
        devices: [commanderId],
      };
      await dispatch(postGroup(group, undefined, groupId ?? ''));
    } catch (error) {
      if (typeof error === 'string') {
        errorMessage = error;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      throw new Error(errorMessage);
    }
  };
  return [createMerchandiser] as const;
};
